import { createAsyncThunk } from '@reduxjs/toolkit';
import apiConfig from 'constants/apiConfig';
import { fetchCampaignInvitedCreators } from 'services/api/campaign';
import { AuthState } from 'slices/auth/reducer';
import { ERROR } from 'types';
import {
    CampaignInvites,
    CampaignInvitesFilters,
    CampaignInvitesSlice,
} from 'types/campaign/invites';
import { generateUniqueKey, getErrorMessage, isFresh } from 'utils/helpers';

export const fetchCampaignInvitesThunk = createAsyncThunk<
    {
        invites: CampaignInvites;
        filters: CampaignInvitesFilters;
        filterId: string;
        campaignId: string;
    },
    { campaignId: string; force?: boolean; filters: CampaignInvitesFilters },
    { rejectValue: string }
>(
    'campaignInvites/fetch',
    async ({ campaignId, force, filters }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_CAMPAIGN_INVITED_CREATORS.staleTime;
            const filterId = generateUniqueKey(filters, ['page']);
            const state = getState() as { campaignInvites: CampaignInvitesSlice; auth: AuthState };
            const agencyId = state.auth.selectedAgencyId;
            if (!agencyId) {
                throw new Error('Agency not selected');
            }

            const cachedList =
                state.campaignInvites.campaigns[campaignId]?.filtered_lists?.[filterId];

            if (
                filters.page === 1 &&
                !force &&
                cachedList &&
                !cachedList.expired &&
                isFresh(cachedList.last_fetched_at, STALE_TIME)
            ) {
                return rejectWithValue(ERROR.CACHED);
            }

            const requestFilters = Object.fromEntries(
                Object.entries(filters).filter(
                    ([, value]) => value !== '' && value !== 'all' && value !== undefined && value !== null
                )
            );

            const invites = await fetchCampaignInvitedCreators(agencyId, campaignId, requestFilters);
            return { invites, filters, filterId, campaignId };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch campaign invites'));
        }
    }
);
